import startCase from 'lodash/startCase';
import { Fragment, useState } from 'react';
import { Link } from 'react-router';
import { CaretRightIcon, InfoIcon } from '@phosphor-icons/react';
import type {
  OrderContent,
  OrderCustomItem,
  OrderDetail,
  ReturnDisposition,
} from '@skupreme/api-contracts/core';
import {
  Badge,
  Button,
  Card,
  Popover,
  Image,
  ItemIdentifiers,
} from '@skupreme/shared/ui';
import { VIEW_STATE_LABELS } from '@features/orders';
import { CommonUtility } from '@utility/common';
import { OrderStatus } from '@utility/constants/common';
import { sizedImage } from '../../../lib/listingImage';
import { catalogItemId } from '../lib/shipments';
import { formatOrderStatus, getOrderStatusTone } from '../lib/status';

type OrderItemsData = Pick<OrderDetail, 'currency_code'> &
  Partial<
    Pick<
      OrderDetail,
      | 'custom_items'
      | 'order_contents'
      | 'sales_proceeds'
      | 'summary'
      | 'view_state'
    >
  >;

export type OrderItemsCardProps = {
  order?: OrderItemsData;
  isLoading?: boolean;
  returnStatus?: Partial<Record<string, ReturnDisposition>>;
  onReturnStatusChange?: (itemId: string, status: ReturnDisposition) => void;
  onMatchItem?: (itemKey: string) => void;
  /** A V2 order: its lines say the order's page state, and totals the channel did not send stay off. */
  isV2?: boolean;
};

const RETURN_STATE_MASKS: Record<ReturnDisposition, string[]> = {
  discard: ['100', '110'],
  refurbish: ['010', '110', '011'],
  fulfill: ['001', '011'],
};

function ItemStatus({ status, label }: { status?: string; label?: string }) {
  return (
    <Badge tone={getOrderStatusTone(status)} className="sk:self-start">
      {label || formatOrderStatus(status) || 'Unknown'}
    </Badge>
  );
}

/*
 * EN-4544: a V2 line's status is its order's page state, in the words of the orders list and the summary card
 * (Unfulfilled, In Progress, Shipped, Historical), and Shipped once the line itself left in a shipment.
 */
function lineStatus(
  item: OrderContent,
  viewState: string | null | undefined,
  isV2: boolean | undefined,
): { status?: string; label?: string } {
  const status = item.content.item_status;
  if (!isV2) {
    return { status };
  }
  if (item.content.shipped_time || item.content.tracking_number) {
    return { status: OrderStatus.Shipped, label: 'Shipped' };
  }
  return {
    status,
    label: viewState ? VIEW_STATE_LABELS[viewState] : undefined,
  };
}

const lineSubtotal = (item: OrderContent) =>
  item.content.proceeds?.item_subtotal ??
  (item.content.unit_price || 0) * (item.content.qty || 0);

function money(value: number | undefined, currency?: string) {
  return CommonUtility.currencyFormat(value || 0, currency);
}

function ReturnActions({
  item,
  selectedStatus,
  onChange,
}: {
  item: OrderContent;
  selectedStatus?: ReturnDisposition;
  onChange: (itemId: string, status: ReturnDisposition) => void;
}) {
  const itemId = item.content.item_id;
  const currentStatus = item.content.return_state;
  const statuses: Array<{ value: ReturnDisposition; label: string }> = [
    { value: 'discard', label: 'Discard' },
    { value: 'refurbish', label: 'Refurbish' },
    { value: 'fulfill', label: 'Fulfillable' },
  ];

  return (
    <div className="sk:flex sk:flex-wrap sk:items-center sk:gap-1.5">
      {statuses.map(status => {
        const isCurrent =
          selectedStatus === status.value ||
          (!selectedStatus &&
            currentStatus !== undefined &&
            RETURN_STATE_MASKS[status.value].includes(currentStatus));
        const canChange =
          status.value === 'refurbish'
            ? !currentStatus
            : !currentStatus || ['010', 'refurbish'].includes(currentStatus);

        if (isCurrent && !selectedStatus) {
          return (
            <Badge
              key={status.value}
              tone="info"
              className="sk:text-xs sk:font-normal"
            >
              {status.label}
            </Badge>
          );
        }

        if (!canChange && selectedStatus !== status.value) {
          return null;
        }

        return (
          <Button
            key={status.value}
            size="sm"
            variant={selectedStatus === status.value ? 'primary' : 'pill'}
            onPress={() => onChange(itemId, status.value)}
          >
            {status.label}
          </Button>
        );
      })}
    </div>
  );
}

function ItemFinancialDetails({
  item,
  currency,
}: {
  item: OrderContent;
  currency?: string;
}) {
  const proceeds = item.content.proceeds;

  if (!proceeds) {
    return null;
  }

  const title = item.content.title || item.p_title || 'item';
  const financeEntries = Object.entries(proceeds.finance || {}).filter(
    ([key]) => key !== 'sku' && key !== 'item_id',
  );
  const details = [
    {
      id: 'item-subtotal',
      label: 'Item Subtotal',
      value: proceeds.item_subtotal,
    },
    ...financeEntries.map(([key, value]) => ({
      id: key,
      label: startCase(key),
      value,
    })),
    {
      id: 'grand-subtotal',
      label: 'Grand Subtotal',
      value: proceeds.grand_subtotal,
    },
  ];

  return (
    <Popover
      aria-label={`Financial details for ${title}`}
      placement="bottom end"
      trigger={
        <Button
          variant="ghost"
          shape="circle"
          size="sm"
          aria-label={`View financial details for ${title}`}
        >
          <InfoIcon size={12} />
        </Button>
      }
    >
      <dl className="sk:m-0 sk:grid sk:min-w-48 sk:grid-cols-[auto_auto] sk:gap-x-4 sk:gap-y-1">
        {details.map(detail => (
          <Fragment key={detail.id}>
            <dt className="sk:text-fg-secondary">{detail.label}</dt>
            <dd className="sk:m-0 sk:text-right sk:tabular-nums">
              {money(detail.value, currency)}
            </dd>
          </Fragment>
        ))}
      </dl>
    </Popover>
  );
}

function OrderItemRow({
  item,
  currency,
  selectedReturnStatus,
  onReturnStatusChange,
  onMatchItem,
  status,
}: {
  item: OrderContent;
  currency?: string;
  selectedReturnStatus?: ReturnDisposition;
  onReturnStatusChange?: (itemId: string, status: ReturnDisposition) => void;
  onMatchItem?: (itemKey: string) => void;
  status: { status?: string; label?: string };
}) {
  const title = item.content.title || item.p_title || 'Untitled item';
  const catalogId = catalogItemId(item);
  const qty = item.content.qty || 0;
  const unitPrice = item.content.unit_price || 0;
  const subtotal = lineSubtotal(item);
  const itemKey =
    item.p_sku || item.content.line_item_id || item.content.item_id;

  return (
    <div className="sk:flex sk:flex-col sk:gap-2 sk:border-b sk:border-rule sk:py-3 sk:last:border-b-0">
      <div className="sk:flex sk:min-w-0 sk:items-center sk:gap-3">
        <Image
          src={sizedImage(item.p_images?.display_image?.trim(), 'thumb')}
          alt=""
          loading="lazy"
        />

        <div className="sk:flex sk:min-w-0 sk:flex-1 sk:flex-col sk:gap-1">
          {catalogId ? (
            <Link
              to={`/app/catalogs/catalog/${encodeURIComponent(catalogId)}`}
              className="sk:truncate sk:text-dense sk:font-medium sk:text-link sk:hover:text-link sk:hover:underline"
              title={title}
            >
              {title}
            </Link>
          ) : (
            <span className="sk:truncate sk:text-dense sk:font-medium sk:text-fg">
              {title}
            </span>
          )}
          <ItemIdentifiers
            itemTitle={title}
            identifiers={[
              { label: 'SKU', value: item.content.sku || item.p_sku },
              { label: 'UPC', value: item.upc },
            ]}
          />
          <ItemStatus status={status.status} label={status.label} />
        </div>

        <span className="sk:min-w-20 sk:text-right sk:text-dense sk:text-fg-secondary">
          {qty} × {money(unitPrice, currency)}
        </span>
        <span className="sk:flex sk:min-w-20 sk:items-center sk:justify-end sk:gap-1">
          <span className="sk:text-right sk:text-dense sk:font-semibold sk:text-fg">
            {money(subtotal, currency)}
          </span>
          <ItemFinancialDetails item={item} currency={currency} />
        </span>
      </div>

      {item.content.item_status === 'Returns' && onReturnStatusChange ? (
        <div className="sk:pl-13">
          <ReturnActions
            item={item}
            selectedStatus={selectedReturnStatus}
            onChange={onReturnStatusChange}
          />
        </div>
      ) : null}

      {!item._id && onMatchItem && itemKey ? (
        <div className="sk:pl-13">
          <Button size="sm" onPress={() => onMatchItem(itemKey)}>
            Match Item
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function CustomItemRow({
  item,
  currency,
}: {
  item: OrderCustomItem;
  currency?: string;
}) {
  const total = (item.price || 0) * (item.quantity || 0);

  return (
    <div className="sk:flex sk:min-w-0 sk:items-center sk:gap-3 sk:border-b sk:border-rule sk:py-3 sk:last:border-b-0">
      <span className="sk:size-10 sk:shrink-0 sk:rounded-option sk:bg-fill-tertiary" />
      <div className="sk:flex sk:min-w-0 sk:flex-1 sk:flex-col sk:gap-1">
        <span className="sk:truncate sk:text-dense sk:font-medium sk:text-fg">
          {item.item_name}
        </span>
        <span className="sk:text-xs sk:text-fg-tertiary">Custom item</span>
        {item.state ? <ItemStatus status={item.state} /> : null}
      </div>
      <span className="sk:min-w-20 sk:text-right sk:text-dense sk:text-fg-secondary">
        {item.quantity || 0} × {money(item.price, currency)}
      </span>
      <span className="sk:min-w-20 sk:text-right sk:text-dense sk:font-semibold sk:text-fg">
        {money(total, currency)}
      </span>
    </div>
  );
}

function TotalRow({
  label,
  value,
  currency,
}: {
  label: string;
  value: number | undefined;
  currency?: string;
}) {
  return (
    <div className="sk:flex sk:items-center sk:justify-between sk:gap-4 sk:text-dense">
      <span className="sk:text-fg-secondary">{label}</span>
      <span className="sk:font-medium sk:text-fg">
        {money(value, currency)}
      </span>
    </div>
  );
}

/**
 * Marketplaces report fees and adjustments long after fulfillment, so the
 * breakdown stays folded away until asked for and the totals stay readable.
 */
function Fees({ order }: { order: OrderItemsData }) {
  const [isOpen, setIsOpen] = useState(false);
  const rows = Object.entries(order.sales_proceeds?.finance || {})
    .filter(
      ([key, value]) => key !== 'sku' && key !== 'principal' && value !== 0,
    )
    .map(([key, value]) => ({ label: startCase(key), value }));
  const status = order.summary?.status;
  const isFulfilled =
    status === OrderStatus.Shipped || status === OrderStatus.Completed;

  if (rows.length === 0) {
    if (!isFulfilled) {
      return null;
    }

    return (
      <span className="sk:text-xs sk:text-fg-tertiary">
        Fees and adjustments aren&apos;t available yet. They may appear once the
        marketplace reports them, and some marketplaces never do.
      </span>
    );
  }

  const total = rows.reduce((sum, row) => sum + row.value, 0);

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="sk:-mx-2 sk:justify-between sk:px-2 sk:font-normal"
        onPress={() => setIsOpen(!isOpen)}
      >
        <span className="sk:flex sk:items-center sk:gap-1 sk:text-fg-secondary">
          <CaretRightIcon
            size={12}
            weight="bold"
            className={isOpen ? 'sk:rotate-90' : undefined}
          />
          Fees &amp; adjustments
          <span className="sk:text-fg-tertiary">{rows.length}</span>
        </span>
        <span className="sk:font-medium sk:text-fg">
          {money(total, order.currency_code)}
        </span>
      </Button>

      {isOpen ? (
        <div className="sk:flex sk:flex-col sk:gap-2 sk:pl-4">
          {rows.map(row => (
            <TotalRow
              key={row.label}
              label={row.label}
              value={row.value}
              currency={order.currency_code}
            />
          ))}
        </div>
      ) : null}
    </>
  );
}

function Totals({ order, isV2 }: { order: OrderItemsData; isV2?: boolean }) {
  const customTotal = (order.custom_items || []).reduce(
    (total, item) => total + (item.price || 0) * (item.quantity || 0),
    0,
  );
  const proceeds = order.sales_proceeds;

  if (!proceeds) {
    return null;
  }
  /* EN-4544: a V2 order whose channel sent no subtotal adds up its lines; shipping and tax it did not send stay off
     rather than read $0.00. */
  const itemsTotal =
    isV2 && !proceeds.items_total
      ? (order.order_contents || []).reduce(
          (total, item) => total + lineSubtotal(item),
          0,
        )
      : proceeds.items_total || 0;
  const known = (value: number | null | undefined) =>
    !isV2 || (value !== null && value !== undefined);

  return (
    <div className="sk:-mx-4 sk:-mb-1 sk:mt-1 sk:flex sk:w-auto sk:flex-col sk:gap-2 sk:px-4 sk:py-4">
      <TotalRow
        label="Subtotal"
        value={itemsTotal + customTotal}
        currency={order.currency_code}
      />
      {known(proceeds.shipping_total) ? (
        <TotalRow
          label="Shipping"
          value={proceeds.shipping_total}
          currency={order.currency_code}
        />
      ) : null}
      {known(proceeds.tax_total) ? (
        <TotalRow
          label="Tax"
          value={proceeds.tax_total}
          currency={order.currency_code}
        />
      ) : null}
      <Fees order={order} />
      <div className="sk:flex sk:items-center sk:justify-between sk:gap-4 sk:pt-1 sk:text-sm sk:font-semibold">
        <span className="sk:text-fg">Total</span>
        <span className="sk:text-fg">
          {money(proceeds.grand_total + customTotal, order.currency_code)}
        </span>
      </div>
    </div>
  );
}

export function OrderItemsCard({
  order,
  isLoading,
  returnStatus = {},
  onReturnStatusChange,
  onMatchItem,
  isV2,
}: OrderItemsCardProps) {
  const orderItems = order?.order_contents || [];
  const customItems = order?.custom_items || [];
  const lineCount = orderItems.length + customItems.length;
  const itemCount =
    orderItems.reduce((total, item) => total + (item.content.qty || 0), 0) +
    customItems.reduce((total, item) => total + (item.quantity || 0), 0);

  return (
    <Card
      title="Items"
      className="sk:overflow-hidden"
      action={
        itemCount > 0 ? (
          <span
            title={`${itemCount} ${itemCount === 1 ? 'item' : 'items'}`}
            className="sk:inline-flex sk:size-6 sk:items-center sk:justify-center sk:rounded-full sk:bg-fill-secondary sk:text-dense sk:font-medium sk:text-fg-secondary sk:inset-shadow-hairline"
          >
            {itemCount}
          </span>
        ) : null
      }
      bodyClassName="sk:flex sk:flex-col sk:px-4 sk:py-1"
    >
      {isLoading && !order ? (
        <div className="sk:my-3 sk:h-24 sk:animate-pulse sk:rounded-option sk:bg-fill-tertiary" />
      ) : null}

      {orderItems.map(item => (
        <OrderItemRow
          key={item._id || item.content.item_id}
          item={item}
          currency={order?.currency_code}
          selectedReturnStatus={returnStatus[item.content.item_id]}
          onReturnStatusChange={onReturnStatusChange}
          onMatchItem={onMatchItem}
          status={lineStatus(item, order?.view_state, isV2)}
        />
      ))}

      {customItems.map((item, index) => (
        <CustomItemRow
          key={`${item.item_name}-${index}`}
          item={item}
          currency={order?.currency_code}
        />
      ))}

      {!isLoading && order && lineCount === 0 ? (
        <span className="sk:py-3 sk:text-dense sk:text-fg-tertiary">
          No items
        </span>
      ) : null}

      {order ? <Totals order={order} isV2={isV2} /> : null}
    </Card>
  );
}
